import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trash2, Plus, Send, CreditCard, ArrowLeft, Loader2 } from "lucide-react";

const STATUS_COLORS = {
  draft: "bg-slate-200 text-slate-900",
  quoted: "bg-blue-100 text-blue-900",
  invoiced: "bg-amber-100 text-amber-900",
  paid: "bg-emerald-100 text-emerald-900",
  completed: "bg-emerald-100 text-emerald-900",
  cancelled: "bg-red-100 text-red-900",
};

export default function EstimateDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [est, setEst] = useState(null);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/estimates/${id}`);
    setEst(data);
  };
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data));
  }, [id]);

  if (!est) return <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading estimate…</div>;

  const updateLine = (i, patch) => {
    const items = [...est.line_items];
    items[i] = { ...items[i], ...patch };
    items[i].total = Number((items[i].quantity || 0) * (items[i].unit_price || 0)) || 0;
    setEst({ ...est, line_items: items });
  };
  const addLine = () => setEst({ ...est, line_items: [...est.line_items, { description: "New item", quantity: 1, unit: "each", unit_price: 0, total: 0 }] });
  const removeLine = (i) => setEst({ ...est, line_items: est.line_items.filter((_, idx) => idx !== i) });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/estimates/${id}`, {
        line_items: est.line_items,
        tax_rate: est.tax_rate,
        status: est.status,
        notes: est.notes,
        address: est.address,
        client_id: est.client_id,
      });
      setEst(data);
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const markQuoted = async () => {
    await api.patch(`/estimates/${id}`, { status: "quoted" });
    toast.success("Marked as quoted");
    load();
  };

  const checkout = async () => {
    setCheckingOut(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        estimate_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment link failed");
    } finally { setCheckingOut(false); }
  };

  const subtotal = est.line_items.reduce((a, b) => a + (b.total || 0), 0);
  const tax = subtotal * (est.tax_rate || 0) / 100;
  const total = subtotal + tax;

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      <Link to="/app/estimates" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-4 h-4" /> All estimates
      </Link>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-primary mb-1">Estimate {est.id.slice(0, 8)}</p>
          <h1 className="font-display text-4xl font-black tracking-tight">{est.niche.replace(/_/g, " ")}</h1>
          <Badge data-testid="est-status" className={`mt-2 ${STATUS_COLORS[est.status] || ""}`}>{est.status}</Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="save-estimate-btn" variant="outline" className="h-11 rounded-sm" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save changes"}</Button>
          {est.status === "draft" && <Button data-testid="mark-quoted-btn" variant="outline" className="h-11 rounded-sm" onClick={markQuoted}><Send className="w-4 h-4 mr-1" /> Mark quoted</Button>}
          <Button data-testid="checkout-btn" className="h-11 rounded-sm btn-kinetic" onClick={checkout} disabled={checkingOut || est.status === "paid"}>
            <CreditCard className="w-4 h-4 mr-1" /> {est.status === "paid" ? "Paid" : (checkingOut ? "…" : "Charge customer")}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="bento-card p-5">
            <h2 className="font-display text-2xl font-bold mb-3">AI analysis</h2>
            <p className="text-sm leading-relaxed text-muted-foreground mb-4">{est.ai_analysis}</p>
            {est.photo_base64 && (
              <img src={est.photo_base64.startsWith("data:") ? est.photo_base64 : `data:image/jpeg;base64,${est.photo_base64}`} alt="job" className="w-full max-h-80 object-cover border border-border rounded-sm" />
            )}
          </div>

          <div className="bento-card p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl font-bold">Line items</h2>
              <Button data-testid="add-line-btn" variant="outline" size="sm" className="rounded-sm" onClick={addLine}><Plus className="w-4 h-4 mr-1" /> Add</Button>
            </div>
            <div className="space-y-2">
              {est.line_items.map((li, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center border-b border-border pb-2">
                  <Input data-testid={`line-desc-${i}`} className="col-span-12 md:col-span-5 h-10 rounded-sm" value={li.description} onChange={(e) => updateLine(i, { description: e.target.value })} />
                  <Input data-testid={`line-qty-${i}`} className="col-span-4 md:col-span-2 h-10 rounded-sm font-mono" type="number" step="0.01" value={li.quantity} onChange={(e) => updateLine(i, { quantity: parseFloat(e.target.value) || 0 })} />
                  <div className="col-span-4 md:col-span-2 text-xs text-muted-foreground text-center">{li.unit}</div>
                  <Input data-testid={`line-price-${i}`} className="col-span-4 md:col-span-2 h-10 rounded-sm font-mono" type="number" step="0.01" value={li.unit_price} onChange={(e) => updateLine(i, { unit_price: parseFloat(e.target.value) || 0 })} />
                  <div className="col-span-11 md:col-span-1 font-mono text-right font-semibold">${li.total.toFixed(2)}</div>
                  <button data-testid={`del-line-${i}`} onClick={() => removeLine(i)} className="col-span-1 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              {est.line_items.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No line items — click "Add" to build the quote.</p>}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bento-card p-5">
            <h3 className="font-display text-xl font-bold mb-3">Totals</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span className="font-mono font-semibold">${subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between items-center">
                <Label className="text-sm">Tax rate %</Label>
                <Input data-testid="tax-rate-input" type="number" step="0.1" className="w-20 h-8 rounded-sm text-right font-mono" value={est.tax_rate || 0} onChange={(e) => setEst({ ...est, tax_rate: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="flex justify-between"><span>Tax</span><span className="font-mono">${tax.toFixed(2)}</span></div>
              <div className="border-t border-border pt-2 flex justify-between font-display text-2xl font-black"><span>TOTAL</span><span className="text-primary" data-testid="est-total">${total.toFixed(2)}</span></div>
            </div>
          </div>

          <div className="bento-card p-5">
            <h3 className="font-display text-xl font-bold mb-3">Client</h3>
            <Select value={est.client_id || ""} onValueChange={(v) => setEst({ ...est, client_id: v })}>
              <SelectTrigger data-testid="client-select" className="h-11 rounded-sm"><SelectValue placeholder="Assign a client" /></SelectTrigger>
              <SelectContent>
                {clients.length === 0 && <SelectItem value="__none" disabled>No clients yet</SelectItem>}
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="bento-card p-5">
            <h3 className="font-display text-xl font-bold mb-3">Site</h3>
            <Label className="text-xs uppercase text-muted-foreground">Address</Label>
            <Input data-testid="site-address-input" className="h-10 rounded-sm mb-3 mt-1" value={est.address || ""} onChange={(e) => setEst({ ...est, address: e.target.value })} />
            <Label className="text-xs uppercase text-muted-foreground">Notes</Label>
            <Textarea data-testid="site-notes-input" className="rounded-sm mt-1" rows={3} value={est.notes || ""} onChange={(e) => setEst({ ...est, notes: e.target.value })} />
          </div>
        </div>
      </div>
    </div>
  );
}
